import React from "react";
import { Check, X as XIcon } from "lucide-react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import { LeadModalProvider } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import Seo from "@/seo/Seo";

// Capabilities-by-vendor comparison. Booleans show as check/cross.
// Strings ("Partial", "Add-on", etc.) render as text in the cell.
const VENDORS = ["Stori", "Simple Booth", "Snappic", "Hue Photo Lounge", "Curate", "Photobooth Supply Co"];

const CRITERIA = [
  {
    name: "On-device AI photo transformation (sub-2s)",
    rows: { Stori: true, "Simple Booth": false, Snappic: "Cloud-only", "Hue Photo Lounge": false, Curate: false, "Photobooth Supply Co": false },
  },
  {
    name: "Real-time influencer detection (Instagram + TikTok)",
    rows: { Stori: true, "Simple Booth": false, Snappic: false, "Hue Photo Lounge": false, Curate: false, "Photobooth Supply Co": false },
  },
  {
    name: "Marketing automation playbooks",
    rows: { Stori: true, "Simple Booth": "Limited", Snappic: "Partial", "Hue Photo Lounge": false, Curate: false, "Photobooth Supply Co": false },
  },
  {
    name: "Native CRM sync (HubSpot, Salesforce, Klaviyo)",
    rows: { Stori: true, "Simple Booth": "Zapier only", Snappic: "Partial", "Hue Photo Lounge": "Zapier only", Curate: false, "Photobooth Supply Co": false },
  },
  {
    name: "Live TV gallery + moderation",
    rows: { Stori: true, "Simple Booth": true, Snappic: true, "Hue Photo Lounge": "Limited", Curate: true, "Photobooth Supply Co": "Limited" },
  },
  {
    name: "AI-generated portraits & style packs",
    rows: { Stori: true, "Simple Booth": false, Snappic: "Add-on", "Hue Photo Lounge": false, Curate: false, "Photobooth Supply Co": false },
  },
  {
    name: "Orbital 360 boom capture",
    rows: { Stori: true, "Simple Booth": false, Snappic: true, "Hue Photo Lounge": false, Curate: true, "Photobooth Supply Co": true },
  },
  {
    name: "Permanent installation support",
    rows: { Stori: true, "Simple Booth": false, Snappic: "Event only", "Hue Photo Lounge": false, Curate: "Event only", "Photobooth Supply Co": "Event only" },
  },
  {
    name: "Lowest entry price",
    rows: { Stori: "$49.95/mo", "Simple Booth": "$59/mo", Snappic: "$179/mo", "Hue Photo Lounge": "$99/mo", Curate: "Custom", "Photobooth Supply Co": "$59/mo" },
  },
  {
    name: "Founded",
    rows: { Stori: "2013", "Simple Booth": "2010", Snappic: "2014", "Hue Photo Lounge": "2018", Curate: "2014", "Photobooth Supply Co": "2014" },
  },
];

function renderCell(value, isStori) {
  if (value === true) {
    return (
      <span
        className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
          isStori ? "bg-stori-cyan/15 text-stori-cyan" : "bg-emerald-500/10 text-emerald-400"
        }`}
        aria-label="Included"
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    );
  }
  if (value === false) {
    return (
      <span
        className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-stori-border text-stori-muted/60"
        aria-label="Not included"
      >
        <XIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
      </span>
    );
  }
  return <span className="text-xs text-stori-muted">{value}</span>;
}

function CompareHero() {
  return (
    <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 border-b border-stori-border">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          COMPARE
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h1 className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white max-w-4xl mx-auto">
          Stori vs the field.
        </h1>
        <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl mx-auto leading-relaxed">
          A side-by-side comparison of Stori, Simple Booth, Snappic, Hue Photo
          Lounge, Curate, and Photobooth Supply Co — across AI capability,
          marketing automation, integrations, and pricing.
        </p>
      </div>
    </section>
  );
}

export default function Compare() {
  // Lightweight summary blocks for LLM-citable, declarative comparisons.
  const summaryRows = [
    {
      vs: "Stori vs Simple Booth",
      summary:
        "Both offer iPad-based capture, but Stori adds real-time influencer detection, on-device AI transformations, and native CRM sync — features Simple Booth requires third-party tools to approximate.",
    },
    {
      vs: "Stori vs Snappic",
      summary:
        "Snappic and Stori both target the agency-event market. Stori differentiates with on-device AI (sub-2s, no cloud round-trip), built-in playbooks, and permanent-installation support out of the box.",
    },
    {
      vs: "Stori vs Hue Photo Lounge",
      summary:
        "Hue is a hardware-rental brand with included software. Stori is a software-first platform that runs on your own hardware or ours, with deeper AI, analytics, and CRM workflows.",
    },
    {
      vs: "Stori vs Curate",
      summary:
        "Curate focuses on event-rental orbital and AI booths. Stori offers comparable hardware plus a software platform you can license for permanent installations in hotels, restaurants, and retail.",
    },
    {
      vs: "Stori vs Photobooth Supply Co",
      summary:
        "Photobooth Supply Co is primarily a hardware retailer with companion software. Stori is a software platform — bring your own iPad or use ours — with AI, influencer detection, and automation built in.",
    },
  ];

  return (
    <LeadModalProvider>
      <Seo path="/compare" />
      <main data-testid="compare-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <CompareHero />

        <section className="relative py-20 lg:py-28">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <div className="rounded-2xl border border-stori-border overflow-hidden overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-stori-surface1 border-b border-stori-border">
                    <th className="text-left font-mono text-xs uppercase tracking-[0.18em] text-stori-muted px-5 py-4 min-w-[280px]">
                      Capability
                    </th>
                    {VENDORS.map((v) => (
                      <th
                        key={v}
                        className={`px-4 py-4 font-mono text-xs uppercase tracking-[0.18em] text-center min-w-[140px] ${
                          v === "Stori" ? "text-stori-cyan" : "text-stori-muted"
                        }`}
                      >
                        {v}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CRITERIA.map((c) => (
                    <tr
                      key={c.name}
                      className="border-b border-stori-border last:border-b-0 hover:bg-stori-surface1/40 transition-colors"
                    >
                      <td className="px-5 py-4 text-white">{c.name}</td>
                      {VENDORS.map((v) => (
                        <td key={v} className="px-4 py-4 text-center">
                          {renderCell(c.rows[v], v === "Stori")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-16 grid md:grid-cols-2 gap-6">
              {summaryRows.map((r) => (
                <article
                  key={r.vs}
                  className="p-7 rounded-2xl border border-stori-border bg-stori-surface1"
                >
                  <h2 className="font-heading text-xl font-semibold text-white tracking-tight">
                    {r.vs}
                  </h2>
                  <p className="mt-3 text-sm text-stori-muted leading-relaxed">
                    {r.summary}
                  </p>
                </article>
              ))}
            </div>

            <p className="mt-12 text-xs text-stori-muted/70 max-w-3xl">
              Comparison data reflects public information available at time of
              writing. Capabilities and pricing change frequently — verify with
              each vendor directly before procurement decisions.
            </p>
          </div>
        </section>

        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
